import { DraftRestorePrompt } from '@/components/DraftRestorePrompt';
import { createIncident, listPetsByCustomer, searchCustomers } from '@/lib/api';
import type { Customer, Pet } from '@/lib/types';
import type {
  CreateIncidentInput,
  IncidentSeverity,
  IncidentType,
  OwnerNotificationMethod,
} from '@/lib/types';
import { useDraft } from '@/lib/useDraft';
import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** Autosaved form state — see useDraft in the page body. */
interface IncidentFormState {
  incidentType: IncidentType;
  severity: IncidentSeverity;
  occurredAt: string;
  description: string;
  actionTaken: string;
  vetCalled: boolean;
  ownerNotifyNow: boolean;
  ownerNotifiedAt: string;
  ownerNotificationMethod: OwnerNotificationMethod;
  ownerNotificationNotes: string;
  followUpRequired: boolean;
  followUpDueOn: string;
  followUpNotes: string;
}

const INCIDENT_TYPES: IncidentType[] = [
  'bite',
  'escape',
  'injury',
  'illness',
  'behavioral',
  'property_damage',
  'lost_item',
  'other',
];

const INCIDENT_SEVERITIES: IncidentSeverity[] = ['minor', 'moderate', 'severe', 'critical'];

const OWNER_NOTIFICATION_METHODS: OwnerNotificationMethod[] = [
  'phone',
  'email',
  'sms',
  'whatsapp',
  'in_person',
];

function formatType(t: IncidentType): string {
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// PetPicker — customer search + pet selection
// ---------------------------------------------------------------------------

interface PetPickerProps {
  onSelect: (pet: Pet, customer: Customer) => void;
}

function PetPicker({ onSelect }: PetPickerProps) {
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [loadingPets, setLoadingPets] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleQueryChange(v: string) {
    setQuery(v);
    setSelectedCustomer(null);
    setPets([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v.trim()) {
      setCustomers([]);
      setShowDropdown(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchCustomers(v.trim());
        setCustomers(results.slice(0, 8));
        setShowDropdown(true);
      } catch {
        setCustomers([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }

  async function handleCustomerSelect(c: Customer) {
    setSelectedCustomer(c);
    setQuery(`${c.firstName} ${c.lastName}`);
    setShowDropdown(false);
    setLoadingPets(true);
    try {
      const result = await listPetsByCustomer(c.id);
      setPets(result);
    } catch {
      setPets([]);
    } finally {
      setLoadingPets(false);
    }
  }

  return (
    <div className="space-y-3">
      {/* Customer search */}
      <div ref={containerRef} className="relative">
        <label className="block text-sm font-medium mb-1" htmlFor="customerSearch">
          Customer <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground"
            strokeWidth={1.75}
          />
          <input
            id="customerSearch"
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => customers.length > 0 && setShowDropdown(true)}
            placeholder="Search by customer name…"
            className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
          {searching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          )}
        </div>
        {showDropdown && customers.length > 0 && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
            {customers.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => void handleCustomerSelect(c)}
                className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-secondary"
              >
                <span className="font-medium">
                  {c.firstName} {c.lastName}
                </span>
                {c.email && <span className="text-xs text-muted-foreground">{c.email}</span>}
              </button>
            ))}
          </div>
        )}
        {showDropdown && !searching && query.trim() && customers.length === 0 && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-muted-foreground shadow-lg">
            No customers found
          </div>
        )}
      </div>

      {/* Pet picker */}
      {selectedCustomer && (
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="petSelect">
            Pet <span className="text-destructive">*</span>
          </label>
          {loadingPets ? (
            <p className="text-sm text-muted-foreground">Loading pets…</p>
          ) : pets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pets found for this customer.</p>
          ) : (
            <div className="relative">
              <select
                id="petSelect"
                defaultValue=""
                onChange={(e) => {
                  const pet = pets.find((p) => p.id === e.target.value);
                  if (pet) onSelect(pet, selectedCustomer);
                }}
                className="w-full appearance-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
              >
                <option value="" disabled>
                  Select a pet…
                </option>
                {pets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.species}
                    {p.breed ? `, ${p.breed}` : ''})
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground"
                strokeWidth={1.75}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function NewIncidentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const prefillPetId = searchParams.get('petId') ?? '';
  const prefillPetName = searchParams.get('petName') ?? '';
  const prefillBookingId = searchParams.get('bookingId') ?? '';
  const prefillBookingRef = searchParams.get('bookingRef') ?? '';

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selected pet (from picker or pre-filled)
  const [selectedPet, setSelectedPet] = useState<{ id: string; name: string } | null>(
    prefillPetId ? { id: prefillPetId, name: prefillPetName || prefillPetId } : null,
  );

  // Whole form autosaves to localStorage (tenant + context keyed) so a
  // frozen tab or accidental navigation mid-report never loses the write-up.
  // `initial` is memoised — an object literal per render would defeat the
  // hook's "follow initial until edited" logic.
  const initialForm = useMemo<IncidentFormState>(
    () => ({
      incidentType: 'bite',
      severity: 'minor',
      occurredAt: new Date().toISOString().slice(0, 16),
      description: '',
      actionTaken: '',
      vetCalled: false,
      ownerNotifyNow: false,
      ownerNotifiedAt: '',
      ownerNotificationMethod: 'phone',
      ownerNotificationNotes: '',
      followUpRequired: false,
      followUpDueOn: '',
      followUpNotes: '',
    }),
    [],
  );
  const draft = useDraft<IncidentFormState>(
    `incident:new:${prefillBookingId || prefillPetId || 'any'}`,
    initialForm,
    { isEmpty: (f) => f.description.trim() === '' && f.actionTaken.trim() === '' },
  );
  const patch = (p: Partial<IncidentFormState>) => draft.setValue((prev) => ({ ...prev, ...p }));
  const {
    incidentType,
    severity,
    occurredAt,
    description,
    actionTaken,
    vetCalled,
    ownerNotifyNow,
    ownerNotifiedAt,
    ownerNotificationMethod,
    ownerNotificationNotes,
    followUpRequired,
    followUpDueOn,
    followUpNotes,
  } = draft.value;
  const setIncidentType = (v: IncidentType) => patch({ incidentType: v });
  const setSeverity = (v: IncidentSeverity) => patch({ severity: v });
  const setOccurredAt = (v: string) => patch({ occurredAt: v });
  const setDescription = (v: string) => patch({ description: v });
  const setActionTaken = (v: string) => patch({ actionTaken: v });
  const setVetCalled = (v: boolean) => patch({ vetCalled: v });
  const setOwnerNotifyNow = (v: boolean) => patch({ ownerNotifyNow: v });
  const setOwnerNotifiedAt = (v: string) => patch({ ownerNotifiedAt: v });
  const setOwnerNotificationMethod = (v: OwnerNotificationMethod) =>
    patch({ ownerNotificationMethod: v });
  const setOwnerNotificationNotes = (v: string) => patch({ ownerNotificationNotes: v });
  const setFollowUpRequired = (v: boolean) => patch({ followUpRequired: v });
  const setFollowUpDueOn = (v: string) => patch({ followUpDueOn: v });
  const setFollowUpNotes = (v: string) => patch({ followUpNotes: v });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedPet) {
      setError('Please select a pet.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateIncidentInput = {
        petId: selectedPet.id,
        incidentType,
        severity,
        occurredAt: `${occurredAt}:00Z`,
        description,
        actionTaken: actionTaken || null,
        vetCalled,
        followUpRequired,
        followUpNotes: followUpNotes || null,
        followUpDueOn: followUpDueOn || null,
      };
      if (prefillBookingId) input.bookingId = prefillBookingId;
      if (ownerNotifyNow && ownerNotifiedAt) {
        input.ownerNotifiedAt = `${ownerNotifiedAt}:00Z`;
        input.ownerNotificationMethod = ownerNotificationMethod;
        input.ownerNotificationNotes = ownerNotificationNotes || null;
      }
      const created = await createIncident(input);
      draft.clearDraft();
      navigate(`/incidents/${created.id}`);
    } catch (e) {
      setError((e as Error).message ?? 'Failed to create incident');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-[100dvh]">
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold">Report incident</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Record an incident involving a pet in your care.
          </p>
        </header>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
          {draft.pendingDraft !== null && (
            <DraftRestorePrompt onRestore={draft.restoreDraft} onDiscard={draft.discardDraft} />
          )}
          {error && (
            <div className="rounded-lg bg-destructive/10 text-destructive px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {/* Context banner when navigated from a booking */}
          {prefillBookingId && (
            <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Linked booking
              </p>
              <p className="text-sm font-medium">
                {prefillBookingRef || `#${prefillBookingId.slice(-8).toUpperCase()}`}
              </p>
            </div>
          )}

          {/* Pet selection */}
          {selectedPet ? (
            <div>
              <p className="text-sm font-medium mb-1">Pet</p>
              <div className="flex items-center justify-between rounded-md border border-border bg-secondary/50 px-3 py-2">
                <span className="text-sm font-medium">{selectedPet.name}</span>
                {!prefillPetId && (
                  <button
                    type="button"
                    onClick={() => setSelectedPet(null)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Change pet"
                  >
                    <X className="size-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <PetPicker onSelect={(pet) => setSelectedPet({ id: pet.id, name: pet.name })} />
          )}

          {/* Type + severity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="incidentType">
                Type <span className="text-destructive">*</span>
              </label>
              <select
                id="incidentType"
                value={incidentType}
                onChange={(e) => setIncidentType(e.target.value as IncidentType)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {INCIDENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {formatType(t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="severity">
                Severity <span className="text-destructive">*</span>
              </label>
              <select
                id="severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {INCIDENT_SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Occurred at */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="occurredAt">
              When did it occur? <span className="text-destructive">*</span>
            </label>
            <input
              id="occurredAt"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="description">
              What happened? <span className="text-destructive">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={4}
              maxLength={5000}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Action taken */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="actionTaken">
              Action taken
            </label>
            <textarea
              id="actionTaken"
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              rows={3}
              maxLength={5000}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Vet called */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={vetCalled}
              onChange={(e) => setVetCalled(e.target.checked)}
              className="rounded"
            />
            Vet called
          </label>

          {/* Owner notification */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={ownerNotifyNow}
                onChange={(e) => setOwnerNotifyNow(e.target.checked)}
                className="rounded"
              />
              Record owner notification
            </label>
            {ownerNotifyNow && (
              <div className="space-y-3 pl-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1" htmlFor="ownerNotifiedAt">
                      Notified at
                    </label>
                    <input
                      id="ownerNotifiedAt"
                      type="datetime-local"
                      value={ownerNotifiedAt}
                      onChange={(e) => setOwnerNotifiedAt(e.target.value)}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" htmlFor="ownerNotificationMethod">
                      Method
                    </label>
                    <select
                      id="ownerNotificationMethod"
                      value={ownerNotificationMethod}
                      onChange={(e) =>
                        setOwnerNotificationMethod(e.target.value as OwnerNotificationMethod)
                      }
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      {OWNER_NOTIFICATION_METHODS.map((m) => (
                        <option key={m} value={m}>
                          {m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1" htmlFor="ownerNotificationNotes">
                    Notes
                  </label>
                  <textarea
                    id="ownerNotificationNotes"
                    value={ownerNotificationNotes}
                    onChange={(e) => setOwnerNotificationNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Follow-up */}
          <div className="rounded-lg border border-border p-4 space-y-4">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={followUpRequired}
                onChange={(e) => setFollowUpRequired(e.target.checked)}
                className="rounded"
              />
              Follow-up required
            </label>
            {followUpRequired && (
              <div className="space-y-3 pl-6">
                <div>
                  <label className="block text-sm mb-1" htmlFor="followUpDueOn">
                    Due on
                  </label>
                  <input
                    id="followUpDueOn"
                    type="date"
                    value={followUpDueOn}
                    onChange={(e) => setFollowUpDueOn(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" htmlFor="followUpNotes">
                    Notes
                  </label>
                  <textarea
                    id="followUpNotes"
                    value={followUpNotes}
                    onChange={(e) => setFollowUpNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedPet}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
